import * as React from 'react';
import { FC, useMemo } from 'react';

import { useSupportData } from 'features/support/core/hooks/use-support-data';
import { ICreateSupportCaseInput, ISupportFormData } from 'features/support/core/types';
import { useCreateSupportCase } from 'features/support/hooks/use-create-support-case';
import {
  logSupportFaqInteraction,
  logSupportFormSubmission,
} from 'features/support/utils/analytics';
import { omit } from 'lodash-es';

import DrawerPage from 'components/layout/drawer-page';
import {
  useGetSupportCategoryGroupQuery,
  useGetSupportSubcategoryQuery,
} from 'generated/sanity-graphql';
import { useRoute } from 'hooks/navigation/use-route';
import { useRefundEligibility } from 'hooks/refund-eligibility/use-refund-eligibility';
import { useOrderDetails } from 'hooks/use-order-details';
import { useOrderTimeTimezone } from 'hooks/use-order-time-timezone';
import { useAuthContext } from 'state/auth';
import { useLocale } from 'state/intl';
import { t } from 'state/intl/translations';
import { serializeServiceMode } from 'state/service-mode/serializers';
import { Region } from 'utils/environment/types';
import { formatDateObject } from 'utils/form';

import { ErrorView } from '../error-view';
import { LoadingView } from '../loading-view';
import { ISupportV2FormPageProps } from '../types';

import { SupportV2FormView } from './support-v2-form-view';
import { IFaq } from './types';

/**
 * Displays the support form and thank-you/error messages after submission
 */
const SupportV2FormPage: FC<React.PropsWithChildren<unknown>> = () => {
  const { params } = useRoute<ISupportV2FormPageProps>();
  const { orderId, subcategoryId, categoryId, alertMessage } = params;

  const { region } = useLocale();
  const { user } = useAuthContext();

  const {
    caseReferenceNumber,
    createCaseState,
    createSupportCase,
    createCaseError,
  } = useCreateSupportCase();

  const { supportCategoryGroups, isSupportDataLoading } = useSupportData();
  const {
    data: supportSubcategoryData,
    loading: isSupportSubcategoryloading,
  } = useGetSupportSubcategoryQuery({
    variables: { id: subcategoryId! },
    skip: !subcategoryId,
  });
  const {
    data: supportSupportCategoryGroupData,
    loading: isSupportSupportCategoryGrouploading,
  } = useGetSupportCategoryGroupQuery({
    variables: { id: categoryId! },
    skip: !categoryId,
  });

  const { order, loading: isOrderLoading } = useOrderDetails(orderId);

  const { refundIneligibilityReason } = useRefundEligibility({ orderId });
  const refundReason = refundIneligibilityReason || '';

  const supportCategory = supportSupportCategoryGroupData?.SupportCategoryGroup;
  const primaryCategoryName = supportCategory?.name?.locale ?? '';
  const showRestaurantSelector = supportCategory?.showRestaurantSelector;

  const supportSubcategory = supportSubcategoryData?.SupportSubcategory;
  const knowledgeForceIssueId = supportSubcategory?.knowledgeForceIssueId;
  const subcategoryName = supportSubcategory?.name?.locale ?? '';
  const showServiceModeSelector = supportSubcategory?.supportSubcategoryServiceModeSelector;
  const faqs: IFaq[] | null =
    supportSubcategory?.supportSubcategoryFaqs?.map(faq => {
      return {
        title: faq?.supportFaqTitle?.locale ?? '',
        content: faq?.supportFaqContent?.localeRaw,
        amplitudeEventName: faq?.supportFaqMParticleEvent,
      };
    }) || [];

  const handleFaqInteraction = (isExpanded: boolean, isFirstInteraction: boolean, faq: IFaq) => {
    if (isExpanded && isFirstInteraction && faq.amplitudeEventName) {
      logSupportFaqInteraction(faq.amplitudeEventName);
    }
  };

  const { orderTimeTimezone } = useOrderTimeTimezone(order);

  const initialValues: ISupportFormData = useMemo(
    () => ({
      comments: '',
      email: user?.details?.email ?? '',
      issueId: knowledgeForceIssueId || '',
      name: user?.details?.name ?? '',
      orderDate: order?.createdAt,
      agreesToTermsOfService: false,
      serviceMode: '',
      issueSubCategory: `${primaryCategoryName}: ${subcategoryName}`,
      ...(!!showRestaurantSelector && {
        storeId: '',
        storeAddress: '',
        orderDateTime: {
          month: '',
          day: '',
          year: '',
        },
      }),
    }),
    [
      user,
      knowledgeForceIssueId,
      order,
      primaryCategoryName,
      subcategoryName,
      showRestaurantSelector,
    ]
  );

  const handleSupportFormSubmit = (formData: ISupportFormData) => {
    const { agreesToTermsOfService, orderDateTime, ...sanitizedFormData } = formData;

    if (!agreesToTermsOfService) {
      return;
    }

    // orderDate is just for display, not for submitting to the server
    const sanitizedFormDataWithoutOrderDate = omit(sanitizedFormData, ['orderDate']);

    const userAccountRegion = user?.details?.isoCountryCode ?? null;

    const submissionData: ICreateSupportCaseInput = {
      ...sanitizedFormDataWithoutOrderDate,
      ...(order?.rbiOrderId && { rbiOrderId: order.rbiOrderId }),
      ...(order?.cart?.serviceMode && {
        serviceMode: serializeServiceMode(order?.cart?.serviceMode),
      }),
      ...(showRestaurantSelector && { orderDateTime: formatDateObject(orderDateTime) }),
      userRegion: Region[region],
      userAccountRegion,
      localStoreOrderTime: orderTimeTimezone,
    };

    createSupportCase(submissionData).then(caseRefNumber => {
      // scrollToFeedback(); - not sure if this will need scrolling yet
      logSupportFormSubmission({
        caseRefNumber: caseRefNumber || '',
        formData,
        // @ts-expect-error TS(2322) FIXME: Type '{ readonly __typename?: "SupportSubcategory"... Remove this comment to see the full error message
        userIssue: supportSubcategoryData?.SupportSubcategory,
      });
    });
  };

  if (
    isSupportDataLoading ||
    isSupportSubcategoryloading ||
    isOrderLoading ||
    isSupportSupportCategoryGrouploading
  ) {
    return <LoadingView />;
  }

  if (!supportCategoryGroups || supportCategoryGroups.length === 0) {
    return <ErrorView message={t('looksLikeWereExperiencingIssues')} />;
  }

  return (
    <DrawerPage headerText=" " dismissType="back" helmetTitle={subcategoryName}>
      <SupportV2FormView
        alertMessage={refundReason || alertMessage}
        bigTitle={subcategoryName}
        smallTitle={primaryCategoryName}
        caseReferenceNumber={caseReferenceNumber}
        createCaseError={createCaseError}
        createCaseState={createCaseState}
        faqs={faqs}
        handleSupportFormSubmit={handleSupportFormSubmit}
        initialValues={initialValues}
        orderDate={order?.createdAt}
        serviceMode={serializeServiceMode(order?.cart?.serviceMode)}
        onFaqInteraction={handleFaqInteraction}
        showRestaurantSelector={showRestaurantSelector}
        showServiceModeSelector={showServiceModeSelector}
      />
    </DrawerPage>
  );
};

export default SupportV2FormPage;
